import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/AuthProvider';
import { EmptyState, PageSpinner } from '@/ui';

/** Sends signed-out visitors to the login page, remembering where they were going. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { t } = useTranslation('shell');
  const { signedIn, loading } = useAuth();
  const location = useLocation();
  if (!signedIn) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (loading) return <PageSpinner label={t('errors.signingIn')} />;
  return <>{children}</>;
}

/** Shows a "No access" page when the caller cannot read the route's resource. */
export function RequireResource({ resource, children }: { resource?: string | readonly string[]; children: ReactNode }) {
  const { t } = useTranslation('shell');
  const { permissions } = useAuth();
  if (!permissions.canViewAny(resource)) {
    const keys = (typeof resource === 'string' ? [resource] : (resource ?? [])).map((r) => `${r}:read`).join(' or ');
    return <EmptyState size="page" icon={<Lock />} title={t('errors.noAccess')} description={t('errors.noAccessDescription', { keys })} />;
  }
  return <>{children}</>;
}
